import React, { useRef, useState, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { Download, GitBranch, Maximize, Move } from 'lucide-react';

const ResearchDecisionFlow = ({ dataA, dataB, wizardAComplete, wizardBComplete }) => {
  const diagramRef = useRef(null);
  const containerRef = useRef(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);

  const fitToView = useCallback(() => {
    if (!containerRef.current || !diagramRef.current) return;
    
    const container = containerRef.current;
    const diagram = diagramRef.current;
    
    const containerWidth = container.clientWidth - 40;
    const containerHeight = container.clientHeight - 40;
    const diagramWidth = 800;
    const diagramHeight = 1200;
    
    const scaleX = containerWidth / diagramWidth;
    const scaleY = containerHeight / diagramHeight;
    const newScale = Math.min(scaleX, scaleY, 1);
    
    setScale(newScale);
    setPan({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    fitToView();
    const handleResize = () => fitToView();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fitToView]);

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (e.target.closest('button')) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPan({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (!wizardAComplete || !dataA) {
    return null;
  }

  const paradigmMap = {
    positivism: { label: 'Positivism', color: '#3b82f6', preferredApproach: 'deductive', preferredMethod: 'quantitative' },
    interpretivism: { label: 'Interpretivism', color: '#8b5cf6', preferredApproach: 'inductive', preferredMethod: 'qualitative' },
    pragmatism: { label: 'Pragmatism', color: '#10b981', preferredApproach: 'abductive', preferredMethod: 'mixed_methods' },
    critical_realism: { label: 'Critical Realism', color: '#f59e0b', preferredApproach: 'abductive', preferredMethod: 'mixed_methods' },
    transformative: { label: 'Transformative', color: '#ec4899', preferredApproach: 'inductive', preferredMethod: 'qualitative' }
  };

  const approachMap = {
    deductive: { label: 'Deductive', description: 'Theory → Hypothesis → Testing' },
    inductive: { label: 'Inductive', description: 'Data → Patterns → Theory' },
    abductive: { label: 'Abductive', description: 'Puzzle → Best Explanation' }
  };

  const methodMap = {
    quantitative: { label: 'Quantitative', strategies: ['experiment', 'survey', 'archival_research'] },
    qualitative: { label: 'Qualitative', strategies: ['case_study', 'ethnography', 'grounded_theory', 'narrative', 'action_research'] },
    mixed_methods: { label: 'Mixed Methods', strategies: ['survey', 'case_study', 'action_research'] }
  };

  const strategyMap = {
    experiment: 'Experiment',
    survey: 'Survey',
    case_study: 'Case Study',
    action_research: 'Action Research',
    grounded_theory: 'Grounded Theory',
    ethnography: 'Ethnography',
    archival_research: 'Archival Research',
    narrative: 'Narrative Inquiry'
  };

  const dimensionLabels = {
    ontology: { realist: 'Realism', relativist: 'Relativism', pragmatic: 'Pragmatic' },
    epistemology: { objectivist: 'Objectivism', subjectivist: 'Subjectivism', critical: 'Critical', practical: 'Practical' },
    axiology: { value_free: 'Value-Free', value_laden: 'Value-Laden', value_driven: 'Value-Driven', pragmatic_values: 'Pragmatic' }
  };

  const userParadigm = dataA.philosophy;
  const userApproach = dataA.theoryApproach;
  const userMethod = dataA.methodologicalChoice;
  const userStrategy = dataA.strategy;
  const userDimensions = dataA.philosophicalDimensions || {};

  const paradigmInfo = paradigmMap[userParadigm] || { label: userParadigm, color: '#6b7280' };
  const isApproachAligned = paradigmInfo.preferredApproach === userApproach;
  const isMethodAligned = paradigmInfo.preferredMethod === userMethod;

  const getRelevantStrategies = () => {
    const methodInfo = methodMap[userMethod];
    let strategies = methodInfo ? [...methodInfo.strategies] : ['survey', 'case_study', 'experiment'];
    if (userStrategy && !strategies.includes(userStrategy)) {
      strategies.push(userStrategy);
    }
    return strategies.slice(0, 5);
  };

  const userTimeHorizon = dataA.timeHorizon;
  const userTechniques = dataA.techniques?.dataCollection || [];
  const userSampling = dataA.techniques?.sampling;

  const timeHorizonLabels = {
    cross_sectional: 'Cross-Sectional',
    longitudinal: 'Longitudinal'
  };

  const samplingLabels = {
    simpleRandom: 'Simple Random',
    systematic: 'Systematic',
    stratified: 'Stratified',
    cluster: 'Cluster',
    multistage: 'Multistage',
    pps: 'PPS',
    convenience: 'Convenience',
    purposive: 'Purposive',
    quota: 'Quota',
    snowball: 'Snowball',
    volunteer: 'Volunteer',
    expert: 'Expert',
    theoretical: 'Theoretical',
    homogeneous: 'Homogeneous',
    maximumVariation: 'Maximum Variation',
    criticalCase: 'Critical Case',
    sequential: 'Sequential',
    adaptive: 'Adaptive',
    rds: 'RDS',
    panel: 'Panel'
  };

  const getSamplingDisplay = () => {
    if (!userSampling) return '—';
    if (Array.isArray(userSampling) && userSampling.length > 0) {
      const labels = userSampling.map(s => samplingLabels[s] || s);
      if (labels.length <= 2) {
        return labels.join(', ');
      }
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
    }
    return samplingLabels[userSampling] || userSampling;
  };

  const getSampleSizeDisplay = () => {
    const sampleSizes = dataA.techniques?.sampleSizesPerMethod;
    if (sampleSizes && Object.keys(sampleSizes).length > 0) {
      const sizes = Object.values(sampleSizes).filter(s => s && parseInt(s) > 0);
      if (sizes.length === 1) {
        return `N = ${sizes[0]}`;
      } else if (sizes.length > 1) {
        const total = sizes.reduce((sum, s) => sum + parseInt(s), 0);
        return `N = ${total} (combined from all data sources)`;
      }
    }
    return '—';
  };

  const handleDownloadPNG = async () => {
    if (!diagramRef.current) return;
    try {
      const canvas = await html2canvas(diagramRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });
      const link = document.createElement('a');
      link.download = 'research_decision_flow.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating PNG:', error);
    }
  };

  const DecisionDiamond = ({ label, selected, options, x, y, width = 140, height = 60 }) => (
    <g transform={`translate(${x}, ${y})`}>
      <polygon 
        points={`${width/2},0 ${width},${height/2} ${width/2},${height} 0,${height/2}`}
        fill="#fef3c7"
        stroke="#f59e0b"
        strokeWidth="2"
      />
      <text x={width/2} y={height/2 - 5} textAnchor="middle" fontSize="10" fill="#92400e" fontWeight="600">
        {label}
      </text>
      <text x={width/2} y={height/2 + 10} textAnchor="middle" fontSize="9" fill="#b45309">
        Decision Point
      </text>
    </g>
  );

  const ChoiceBox = ({ label, isSelected, isRecommended, x, y, width = 120, height = 45, color }) => {
    const bgColor = isSelected ? (color || '#3b82f6') : '#f1f5f9';
    const textColor = isSelected ? '#ffffff' : '#64748b';
    const borderColor = isSelected ? (color || '#3b82f6') : (isRecommended ? '#10b981' : '#e2e8f0');
    
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect 
          width={width} 
          height={height} 
          rx="6" 
          fill={bgColor}
          stroke={borderColor}
          strokeWidth={isSelected || isRecommended ? 3 : 1}
          strokeDasharray={isRecommended && !isSelected ? "4,2" : "none"}
        />
        <text x={width/2} y={height/2 + 4} textAnchor="middle" fontSize="11" fill={textColor} fontWeight={isSelected ? "700" : "500"}>
          {label}
        </text>
        {isSelected && (
          <circle cx={width - 10} cy={10} r="8" fill="#10b981" />
        )}
        {isSelected && (
          <text x={width - 10} y={14} textAnchor="middle" fontSize="10" fill="white">✓</text>
        )}
        {isRecommended && !isSelected && (
          <text x={width/2} y={height + 12} textAnchor="middle" fontSize="8" fill="#10b981">
            Recommended
          </text>
        )}
      </g>
    );
  };

  const Arrow = ({ x1, y1, x2, y2, isActive, curved = false }) => {
    const color = '#64748b';
    const strokeWidth = isActive ? 2.5 : 1.5;
    const opacity = isActive ? 1 : 0.5;
    
    if (curved) {
      const midY = (y1 + y2) / 2;
      return (
        <path 
          d={`M${x1},${y1} Q${x1},${midY} ${(x1+x2)/2},${midY} T${x2},${y2}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          opacity={opacity}
          markerEnd="url(#arrowhead)"
        />
      );
    }
    
    return (
      <line 
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
        markerEnd="url(#arrowhead)"
      />
    );
  };

  const relevantStrategies = getRelevantStrategies();

  return (
    <div className="decision-flow-container">
      <div className="diagram-header">
        <h3><GitBranch size={20} /> Research Decision Flow</h3>
        <div className="diagram-controls">
          <button className="btn btn-secondary" onClick={fitToView} title="Fit to View">
            <Maximize size={18} />
            Fit to View
          </button>
          <button className="btn btn-primary" onClick={handleDownloadPNG}>
            <Download size={18} />
            Download as PNG
          </button>
        </div>
      </div>

      <div className="decision-flow-legend">
        <div className="legend-item">
          <span className="legend-box selected"></span>
          <span>Your Selection</span>
        </div>
        <div className="legend-item">
          <span className="legend-box recommended"></span>
          <span>Recommended</span>
        </div>
        <div className="legend-item">
          <span className="legend-box alternative"></span>
          <span>Alternative Option</span>
        </div>
        <div className="legend-item">
          <span className="legend-diamond"></span>
          <span>Decision Point</span>
        </div>
        <div className="legend-item pan-hint">
          <Move size={14} />
          <span>Drag to pan</span>
        </div>
      </div>

      <div 
        className={`diagram-wrapper decision-flow-wrapper pannable ${isDragging ? 'dragging' : ''}`}
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          ref={diagramRef}
          className="decision-flow-diagram pannable-content"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out'
          }}
        >
          <div className="flow-title">
            <h2>Research Design Decision Flow</h2>
            <p>Your path through the methodology choices</p>
          </div>

          <svg viewBox="0 0 800 1150" className="decision-tree-svg">
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
              </marker>
            </defs>

            <text x="400" y="30" textAnchor="middle" fontSize="12" fill="#64748b" fontWeight="600">
              PHILOSOPHICAL FOUNDATIONS
            </text>
            
            <g transform="translate(200, 45)">
              <rect width="400" height="65" rx="8" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />
              <text x="200" y="18" textAnchor="middle" fontSize="11" fill="#64748b" fontWeight="600">Your Philosophical Stance</text>
              <text x="70" y="40" fontSize="10" fill="#1e293b" textAnchor="middle">
                <tspan fontWeight="600">Ontology</tspan>
              </text>
              <text x="70" y="54" fontSize="10" fill="#3b82f6" textAnchor="middle" fontWeight="500">
                {dimensionLabels.ontology[userDimensions.ontology] || '—'}
              </text>
              <text x="200" y="40" fontSize="10" fill="#1e293b" textAnchor="middle">
                <tspan fontWeight="600">Epistemology</tspan>
              </text>
              <text x="200" y="54" fontSize="10" fill="#3b82f6" textAnchor="middle" fontWeight="500">
                {dimensionLabels.epistemology[userDimensions.epistemology] || '—'}
              </text>
              <text x="330" y="40" fontSize="10" fill="#1e293b" textAnchor="middle">
                <tspan fontWeight="600">Axiology</tspan>
              </text>
              <text x="330" y="54" fontSize="10" fill="#3b82f6" textAnchor="middle" fontWeight="500">
                {dimensionLabels.axiology[userDimensions.axiology] || '—'}
              </text>
            </g>

            <Arrow x1={400} y1={115} x2={400} y2={135} isActive={true} />

            <DecisionDiamond label="Research Paradigm" x={330} y={140} />

            <Arrow x1={400} y1={200} x2={160} y2={225} isActive={userParadigm === 'positivism'} />
            <Arrow x1={400} y1={200} x2={300} y2={225} isActive={userParadigm === 'interpretivism'} />
            <Arrow x1={400} y1={200} x2={440} y2={225} isActive={userParadigm === 'pragmatism'} />
            <Arrow x1={400} y1={200} x2={580} y2={225} isActive={userParadigm === 'critical_realism'} />
            <Arrow x1={400} y1={200} x2={720} y2={225} isActive={userParadigm === 'transformative'} />

            <ChoiceBox label="Positivism" isSelected={userParadigm === 'positivism'} x={100} y={230} color="#3b82f6" />
            <ChoiceBox label="Interpretivism" isSelected={userParadigm === 'interpretivism'} x={240} y={230} color="#8b5cf6" />
            <ChoiceBox label="Pragmatism" isSelected={userParadigm === 'pragmatism'} x={380} y={230} color="#10b981" />
            <ChoiceBox label="Critical Realism" isSelected={userParadigm === 'critical_realism'} x={520} y={230} color="#f59e0b" />
            <ChoiceBox label="Transformative" isSelected={userParadigm === 'transformative'} x={660} y={230} color="#ec4899" />

            <Arrow x1={400} y1={285} x2={400} y2={315} isActive={true} />

            <DecisionDiamond label="Theory Approach" x={330} y={320} />

            <Arrow x1={400} y1={380} x2={200} y2={400} isActive={userApproach === 'deductive'} />
            <Arrow x1={400} y1={380} x2={400} y2={400} isActive={userApproach === 'inductive'} />
            <Arrow x1={400} y1={380} x2={600} y2={400} isActive={userApproach === 'abductive'} />

            <ChoiceBox 
              label="Deductive" 
              isSelected={userApproach === 'deductive'} 
              isRecommended={paradigmInfo.preferredApproach === 'deductive' && userApproach !== 'deductive'}
              x={140} y={400} 
            />
            <ChoiceBox 
              label="Inductive" 
              isSelected={userApproach === 'inductive'} 
              isRecommended={paradigmInfo.preferredApproach === 'inductive' && userApproach !== 'inductive'}
              x={340} y={400} 
            />
            <ChoiceBox 
              label="Abductive" 
              isSelected={userApproach === 'abductive'} 
              isRecommended={paradigmInfo.preferredApproach === 'abductive' && userApproach !== 'abductive'}
              x={540} y={400} 
            />

            {!isApproachAligned && (
              <g transform="translate(280, 460)">
                <rect width="240" height="25" rx="4" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1" />
                <text x="120" y="16" textAnchor="middle" fontSize="9" fill="#92400e">
                  ⚠ Non-typical approach for {paradigmInfo.label}
                </text>
              </g>
            )}

            <Arrow x1={400} y1={455} x2={400} y2={495} isActive={true} />

            <DecisionDiamond label="Methodology" x={330} y={500} />

            <Arrow x1={400} y1={560} x2={160} y2={585} isActive={userMethod === 'quantitative'} />
            <Arrow x1={400} y1={560} x2={400} y2={585} isActive={userMethod === 'qualitative'} />
            <Arrow x1={400} y1={560} x2={640} y2={585} isActive={userMethod === 'mixed_methods'} />

            <ChoiceBox 
              label="Quantitative" 
              isSelected={userMethod === 'quantitative'} 
              isRecommended={paradigmInfo.preferredMethod === 'quantitative' && userMethod !== 'quantitative'}
              x={100} y={585} 
              color="#0ea5e9"
            />
            <ChoiceBox 
              label="Qualitative" 
              isSelected={userMethod === 'qualitative'} 
              isRecommended={paradigmInfo.preferredMethod === 'qualitative' && userMethod !== 'qualitative'}
              x={340} y={585} 
              color="#a855f7"
            />
            <ChoiceBox 
              label="Mixed Methods" 
              isSelected={userMethod === 'mixed_methods'} 
              isRecommended={paradigmInfo.preferredMethod === 'mixed_methods' && userMethod !== 'mixed_methods'}
              x={580} y={585} 
              color="#14b8a6"
            />

            {!isMethodAligned && (
              <g transform="translate(280, 645)">
                <rect width="240" height="25" rx="4" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1" />
                <text x="120" y="16" textAnchor="middle" fontSize="9" fill="#92400e">
                  ⚠ Consider alignment with {paradigmInfo.label}
                </text>
              </g>
            )}

            <Arrow x1={400} y1={640} x2={400} y2={680} isActive={true} />

            <DecisionDiamond label="Research Strategy" x={330} y={685} />

            {relevantStrategies.map((strategy, idx) => {
              const totalStrategies = relevantStrategies.length;
              const spacing = 140;
              const startX = 400 - ((totalStrategies - 1) * spacing) / 2;
              const xPos = startX + (idx * spacing) - 55;
              
              return (
                <React.Fragment key={strategy}>
                  <Arrow 
                    x1={400} 
                    y1={745} 
                    x2={xPos + 55} 
                    y2={770} 
                    isActive={userStrategy === strategy} 
                  />
                  <ChoiceBox 
                    label={strategyMap[strategy] || strategy} 
                    isSelected={userStrategy === strategy}
                    x={xPos}
                    y={775}
                    width={110}
                  />
                </React.Fragment>
              );
            })}

            <Arrow x1={400} y1={830} x2={400} y2={860} isActive={true} />

            <DecisionDiamond label="Time Horizon" x={330} y={865} />

            <Arrow x1={400} y1={925} x2={245} y2={945} isActive={userTimeHorizon === 'cross_sectional'} />
            <Arrow x1={400} y1={925} x2={555} y2={945} isActive={userTimeHorizon === 'longitudinal'} />

            <ChoiceBox 
              label="Cross-Sectional" 
              isSelected={userTimeHorizon === 'cross_sectional'}
              x={180} y={945}
              width={130}
              color="#22c55e"
            />
            <ChoiceBox 
              label="Longitudinal" 
              isSelected={userTimeHorizon === 'longitudinal'}
              x={490} y={945}
              width={130}
              color="#22c55e"
            />

            <Arrow x1={400} y1={1000} x2={400} y2={1030} isActive={true} />

            <g transform="translate(200, 1040)">
              <rect width="400" height="80" rx="8" fill="#f0fdf4" stroke="#22c55e" strokeWidth="2" />
              <text x="200" y="20" textAnchor="middle" fontSize="11" fill="#166534" fontWeight="600">
                TECHNIQUES & PROCEDURES
              </text>
              <text x="200" y="40" textAnchor="middle" fontSize="10" fill="#15803d">
                Data Collection: {userTechniques.length > 0 ? userTechniques.slice(0, 2).join(', ') : '—'}
                {userTechniques.length > 2 ? ` +${userTechniques.length - 2} more` : ''}
              </text>
              <text x="200" y="55" textAnchor="middle" fontSize="10" fill="#15803d">
                Sampling: {getSamplingDisplay()} | Sample Size: {getSampleSizeDisplay()}
              </text>
            </g>
          </svg>

          <div className="flow-footer">
            <p>Your Research Path: <strong style={{color: paradigmInfo.color}}>{paradigmInfo.label}</strong> → 
              <strong> {approachMap[userApproach]?.label || userApproach}</strong> → 
              <strong> {methodMap[userMethod]?.label || userMethod}</strong> → 
              <strong> {strategyMap[userStrategy] || userStrategy}</strong> → 
              <strong> {timeHorizonLabels[userTimeHorizon] || userTimeHorizon}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchDecisionFlow;
